import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { StatusBadge, GlassCard, PageLoader } from "@/components/shared";
import { Server, HardDrive, Clock, Box, Layers, TrendingUp, ArrowRight, Rocket, CheckCircle2, PlugZap } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setD(r.data));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);
  if (!d) return <PageLoader />;
  const s = d.summary;

  if (d.servers.length === 0) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10"><Rocket className="h-8 w-8 text-cyan-400" /></div>
        <h1 className="mt-6 font-display text-3xl font-bold text-slate-50">Deploy your first backup server</h1>
        <p className="mx-auto mt-3 max-w-md text-slate-400">You don't have any offsite backup servers yet. Launch one in minutes — pick storage, region and connection.</p>
        <button onClick={() => nav("/app/deploy")} data-testid="empty-deploy-cta" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-6 py-3 font-semibold text-[#06080D] hover:bg-cyan-300">
          Deploy Backup Server <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const provisioning = d.servers.find((x) => x.status === "provisioning");
  const cards = [
    { icon: Server, label: "Backup Server", value: <StatusBadge status={s.status === "none" ? "warning" : s.status} />, sub: `${s.healthy_count}/${s.server_count} online` },
    { icon: HardDrive, label: "Storage", value: `${s.storage_used_tb} / ${s.storage_plan_tb} TB`, sub: `${s.storage_pct}% used · ${s.dedup_ratio}% dedup` },
    { icon: Clock, label: "Last Backup", value: timeAgo(s.last_backup_at), sub: "across all servers" },
    { icon: Box, label: "Protected VMs", value: s.protected_vms, sub: `${s.protected_cts} containers` },
    { icon: Layers, label: "Logical / Actual", value: `${s.logical_tb} / ${s.physical_tb} TB`, sub: `${s.dedup_ratio}% dedup savings` },
    { icon: TrendingUp, label: "30-Day Success", value: `${s.success_rate}%`, sub: "verified backups" },
  ];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-50">Are my backups working?</h1>
          <p className="text-sm text-slate-400">Live health across your offsite backup infrastructure.</p>
        </div>
        <button onClick={() => nav("/app/deploy")} data-testid="deploy-another" className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-cyan-500/50">
          <Rocket className="h-4 w-4" /> Deploy another
        </button>
      </div>

      {provisioning && (
        <Link to={`/app/servers/${provisioning.id}`} className="mb-5 flex items-center gap-3 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 text-cyan-200 hover:border-cyan-500/50">
          <PlugZap className="h-5 w-5 animate-pulse" /> <span className="text-sm"><b>{provisioning.hostname}</b> is provisioning — watch progress →</span>
        </Link>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map((c) => (
          <GlassCard key={c.label} className="p-5">
            <div className="flex items-center gap-2 text-slate-500"><c.icon className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">{c.label}</span></div>
            <div className="mt-3 font-display text-xl font-bold text-slate-50">{c.value}</div>
            <div className="mt-1 text-xs text-slate-500">{c.sub}</div>
          </GlassCard>
        ))}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <GlassCard className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display font-semibold text-slate-100">30-Day Backup Activity</h2>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-1 text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Success</span>
              <span className="flex items-center gap-1 text-amber-400"><span className="h-2 w-2 rounded-full bg-amber-400" /> Warnings</span>
              <span className="flex items-center gap-1 text-rose-400"><span className="h-2 w-2 rounded-full bg-rose-400" /> Failures</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.activity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 10 }} interval={4} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0D111A", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="success" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="warnings" stackId="a" fill="#f59e0b" />
              <Bar dataKey="failures" stackId="a" fill="#f43f5e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="p-6">
          <h2 className="mb-4 font-display font-semibold text-slate-100">Data transferred</h2>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.activity}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00E5FF" stopOpacity={0.4} /><stop offset="100%" stopColor="#00E5FF" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 10 }} interval={6} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0D111A", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="gb" stroke="#00E5FF" strokeWidth={2} fill="url(#g)" name="GB" />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>

      <div className="mt-6">
        <h2 className="mb-3 font-display font-semibold text-slate-100">Your servers</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {d.servers.map((sv) => (
            <Link key={sv.id} to={`/app/servers/${sv.id}`} data-testid={`server-card-${sv.id}`}>
              <GlassCard className="p-5 hover:border-cyan-500/40">
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-slate-100">{sv.name}</div>
                  <StatusBadge status={sv.status} />
                </div>
                <div className="mt-1 font-mono text-xs text-slate-500">{sv.hostname}</div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div><div className="text-slate-500">Region</div><div className="text-slate-200">{sv.region}</div></div>
                  <div><div className="text-slate-500">Storage</div><div className="text-slate-200">{sv.storage_used_tb ?? 0}/{sv.storage_plan_tb} TB</div></div>
                  <div><div className="text-slate-500">VMs</div><div className="text-slate-200">{sv.protected_vms ?? 0}</div></div>
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
